import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ExternalLink, Star, Ticket, Volume2, Clock, Quote } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import usePageMeta from '@/hooks/usePageMeta';
import SponsorCylinder from '@/components/SponsorCylinder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FB_EVENT = 'https://www.facebook.com/events/906013352212479';
const SPONSOR_IMG = '/images/site/sponsor.jpg';

// === AMBIENT AUDIO ENGINE ===
const AmbientAudio = () => {
  const ctxRef = useRef(null);
  const startedRef = useRef(false);
  const [active, setActive] = useState(false);

  const startAudio = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setActive(true);

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    ctxRef.current = ctx;
    const master = ctx.createGain();
    master.gain.value = 0.22;
    master.connect(ctx.destination);

    // -- BONFIRE: layered brown noise + low rumble + random crackle pops --
    // Brown noise base (warmer than white noise)
    const brownLen = ctx.sampleRate * 8;
    const brownBuf = ctx.createBuffer(1, brownLen, ctx.sampleRate);
    const brownData = brownBuf.getChannelData(0);
    let lastBrown = 0;
    for (let i = 0; i < brownLen; i++) {
      const white = Math.random() * 2 - 1;
      lastBrown = (lastBrown + 0.02 * white) / 1.02;
      brownData[i] = lastBrown * 3.5;
    }
    const brownSrc = ctx.createBufferSource();
    brownSrc.buffer = brownBuf;
    brownSrc.loop = true;
    const fireLow = ctx.createBiquadFilter();
    fireLow.type = 'lowpass';
    fireLow.frequency.value = 600;
    const fireGain = ctx.createGain();
    fireGain.gain.value = 0.4;
    brownSrc.connect(fireLow);
    fireLow.connect(fireGain);
    fireGain.connect(master);
    brownSrc.start();

    // Crackle layer (sparse random pops)
    const scheduleCrackle = () => {
      if (!ctxRef.current) return;
      const c = ctxRef.current;
      const now = c.currentTime;
      const popDur = 0.01 + Math.random() * 0.025;
      const popBuf = c.createBuffer(1, Math.ceil(c.sampleRate * popDur), c.sampleRate);
      const popData = popBuf.getChannelData(0);
      for (let i = 0; i < popData.length; i++) {
        popData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (c.sampleRate * 0.005));
      }
      const pop = c.createBufferSource();
      pop.buffer = popBuf;
      const popHP = c.createBiquadFilter();
      popHP.type = 'highpass';
      popHP.frequency.value = 1000 + Math.random() * 2000;
      const popG = c.createGain();
      popG.gain.value = 0.06 + Math.random() * 0.1;
      pop.connect(popHP);
      popHP.connect(popG);
      popG.connect(master);
      pop.start(now);
      setTimeout(scheduleCrackle, 100 + Math.random() * 600);
    };
    scheduleCrackle();

    // -- CRICKETS: noise-based trills (realistic wing-scrape texture) --
    const scheduleCricket = (centerFreq, bw, vol, trillRate, minPause, maxPause) => {
      const doCricket = () => {
        if (!ctxRef.current) return;
        const c = ctxRef.current;
        const now = c.currentTime;
        // Each burst: a rapid trill of 8-30 tiny noise pulses
        const pulseCount = 8 + Math.floor(Math.random() * 22);
        const pulseInterval = 1 / (trillRate + (Math.random() - 0.5) * 10);
        const burstDuration = pulseCount * pulseInterval + 0.02;

        // Create one noise buffer for the whole burst
        const bufLen = Math.ceil(c.sampleRate * burstDuration);
        const buf = c.createBuffer(1, bufLen, c.sampleRate);
        const data = buf.getChannelData(0);

        // Fill with silence, then stamp noise pulses at trill rate
        for (let p = 0; p < pulseCount; p++) {
          const startSample = Math.floor(p * pulseInterval * c.sampleRate);
          const pulseSamples = Math.floor(0.004 * c.sampleRate); // 4ms each pulse
          for (let i = 0; i < pulseSamples && (startSample + i) < bufLen; i++) {
            const env = 1 - (i / pulseSamples); // quick decay
            data[startSample + i] = (Math.random() * 2 - 1) * env;
          }
        }

        const src = c.createBufferSource();
        src.buffer = buf;
        // Bandpass to give it the right pitch character
        const bp = c.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = centerFreq + (Math.random() - 0.5) * 400;
        bp.Q.value = bw;
        // Second filter for extra shaping
        const bp2 = c.createBiquadFilter();
        bp2.type = 'bandpass';
        bp2.frequency.value = centerFreq * 1.02;
        bp2.Q.value = bw * 0.7;
        const g = c.createGain();
        g.gain.value = vol;
        src.connect(bp);
        bp.connect(bp2);
        bp2.connect(g);
        g.connect(master);
        src.start(now);

        const pause = minPause + Math.random() * (maxPause - minPause);
        setTimeout(doCricket, pause * 1000);
      };
      setTimeout(doCricket, 1000 + Math.random() * 4000);
    };
    // Three crickets at different pitches, trill speeds, and timing
    scheduleCricket(4800, 8, 0.08, 45, 0.8, 3.5);   // fast high cricket
    scheduleCricket(3800, 6, 0.06, 35, 2.0, 6.0);    // slower mid cricket
    scheduleCricket(5500, 10, 0.05, 55, 3.0, 9.0);   // distant high cricket

    // -- DISTANT COUGH: throaty double-tap --
    const scheduleCough = () => {
      const delay = 20 + Math.random() * 50;
      setTimeout(() => {
        if (!ctxRef.current) return;
        const c = ctxRef.current;
        const now = c.currentTime;
        // Two quick bursts for a natural cough
        for (let burst = 0; burst < 2; burst++) {
          const t = now + burst * 0.12;
          const dur = 0.06 + Math.random() * 0.05;
          const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
          const data = buf.getChannelData(0);
          for (let i = 0; i < data.length; i++) {
            const env = Math.exp(-i / (c.sampleRate * 0.02)) * (burst === 0 ? 1 : 0.5);
            data[i] = (Math.random() * 2 - 1) * env;
          }
          const src = c.createBufferSource();
          src.buffer = buf;
          const bp = c.createBiquadFilter();
          bp.type = 'bandpass';
          bp.frequency.value = 600 + Math.random() * 300;
          bp.Q.value = 1.5;
          const lp = c.createBiquadFilter();
          lp.type = 'lowpass';
          lp.frequency.value = 1800;
          const g = c.createGain();
          g.gain.value = 0.03 + Math.random() * 0.02;
          src.connect(bp);
          bp.connect(lp);
          lp.connect(g);
          g.connect(master);
          src.start(t);
        }
        scheduleCough();
      }, delay * 1000);
    };
    scheduleCough();
  }, []);

  useEffect(() => {
    const handleInteraction = () => {
      if (window.scrollY > 10 || startedRef.current) {
        startAudio();
        window.removeEventListener('scroll', handleInteraction);
        window.removeEventListener('click', handleInteraction);
        window.removeEventListener('touchstart', handleInteraction);
      }
    };
    const handleScroll = () => {
      if (window.scrollY > 10) handleInteraction();
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      if (ctxRef.current) {
        ctxRef.current.close();
        ctxRef.current = null;
      }
    };
  }, [startAudio]);

  return active ? (
    <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 border border-white/10 text-gray-400 text-xs" data-testid="ambient-audio-indicator">
      <Volume2 className="w-3 h-3 animate-pulse" /> Ambient
    </div>
  ) : null;
};

// === GALACTIC SKY CANVAS ===
const GalacticSky = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let dpr = window.devicePixelRatio || 1;

    let w, h;
    const stars = [];
    const nebulaBlobs = [];
    let shootingStars = [];

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // Star color palette inspired by the flyer
    const starColors = [
      [255, 255, 250],   // warm white
      [200, 220, 255],   // blue-white
      [180, 200, 255],   // cool blue
      [255, 240, 220],   // warm amber
      [220, 200, 255],   // lavender
      [196, 157, 77],    // brand gold (rare)
    ];

    // Generate dense star field (500+ stars)
    for (let i = 0; i < 550; i++) {
      const colorIdx = i < 520 ? Math.floor(Math.random() * 5) : 5; // rare gold stars
      const [cr, cg, cb] = starColors[colorIdx];
      const size = Math.random();
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: size < 0.7 ? Math.random() * 0.8 + 0.2 : (size < 0.93 ? Math.random() * 1.5 + 0.8 : Math.random() * 2.2 + 1.4),
        cr, cg, cb,
        twinkleSpeed: Math.random() * 0.03 + 0.004,
        twinkleOffset: Math.random() * Math.PI * 2,
        brightness: Math.random() * 0.4 + 0.6,
        hasHalo: size > 0.9,
      });
    }

    // Nebula blobs for that galactic cloud look
    const nebulaColors = [
      { r: 40, g: 20, b: 120, a: 0.12 },   // deep purple
      { r: 20, g: 30, b: 100, a: 0.10 },    // dark blue
      { r: 80, g: 20, b: 100, a: 0.08 },    // magenta-purple
      { r: 30, g: 50, b: 130, a: 0.10 },    // royal blue
      { r: 100, g: 30, b: 80, a: 0.06 },    // pink-purple
      { r: 15, g: 40, b: 80, a: 0.09 },     // midnight blue
      { r: 60, g: 10, b: 90, a: 0.07 },     // violet
    ];
    for (let i = 0; i < 12; i++) {
      const color = nebulaColors[i % nebulaColors.length];
      nebulaBlobs.push({
        x: Math.random() * w,
        y: Math.random() * h * 0.8,
        rx: Math.random() * 300 + 150,
        ry: Math.random() * 200 + 100,
        rotation: Math.random() * Math.PI,
        color,
        driftX: (Math.random() - 0.5) * 0.08,
        driftY: (Math.random() - 0.5) * 0.04,
        pulseSpeed: Math.random() * 0.008 + 0.003,
        pulseOffset: Math.random() * Math.PI * 2,
      });
    }

    // Milky Way band parameters
    const milkyWay = {
      y: h * 0.35,
      width: h * 0.25,
      tilt: -0.15,
    };

    let time = 0;
    const draw = () => {
      time += 1;
      ctx.clearRect(0, 0, w, h);

      // Base gradient: deep space (matches flyer palette)
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#030012');
      bg.addColorStop(0.25, '#0a0428');
      bg.addColorStop(0.5, '#0d0835');
      bg.addColorStop(0.75, '#080520');
      bg.addColorStop(1, '#050310');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Milky Way band (soft luminous stripe)
      ctx.save();
      ctx.translate(w / 2, milkyWay.y);
      ctx.rotate(milkyWay.tilt);
      const mwGrad = ctx.createLinearGradient(0, -milkyWay.width, 0, milkyWay.width);
      mwGrad.addColorStop(0, 'rgba(30, 20, 80, 0)');
      mwGrad.addColorStop(0.3, 'rgba(50, 35, 110, 0.06)');
      mwGrad.addColorStop(0.5, 'rgba(70, 50, 130, 0.10)');
      mwGrad.addColorStop(0.7, 'rgba(50, 35, 110, 0.06)');
      mwGrad.addColorStop(1, 'rgba(30, 20, 80, 0)');
      ctx.fillStyle = mwGrad;
      ctx.fillRect(-w, -milkyWay.width, w * 2, milkyWay.width * 2);
      ctx.restore();

      // Draw nebula clouds with subtle animation
      nebulaBlobs.forEach(nb => {
        const pulse = Math.sin(time * nb.pulseSpeed + nb.pulseOffset) * 0.3 + 0.7;
        const cx = nb.x + Math.sin(time * 0.002 + nb.pulseOffset) * 20;
        const cy = nb.y + Math.cos(time * 0.0015 + nb.pulseOffset) * 10;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(nb.rotation + time * 0.0001);
        ctx.scale(1, nb.ry / nb.rx);

        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, nb.rx);
        const a = nb.color.a * pulse;
        grad.addColorStop(0, `rgba(${nb.color.r}, ${nb.color.g}, ${nb.color.b}, ${a})`);
        grad.addColorStop(0.4, `rgba(${nb.color.r}, ${nb.color.g}, ${nb.color.b}, ${a * 0.6})`);
        grad.addColorStop(1, `rgba(${nb.color.r}, ${nb.color.g}, ${nb.color.b}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, nb.rx, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // Draw stars
      stars.forEach(s => {
        const twinkle = Math.sin(time * s.twinkleSpeed + s.twinkleOffset) * 0.35 + 0.65;
        const alpha = s.brightness * twinkle;

        // Halo glow for bright stars
        if (s.hasHalo) {
          const haloGrad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r * 6);
          haloGrad.addColorStop(0, `rgba(${s.cr}, ${s.cg}, ${s.cb}, ${alpha * 0.25})`);
          haloGrad.addColorStop(1, `rgba(${s.cr}, ${s.cg}, ${s.cb}, 0)`);
          ctx.fillStyle = haloGrad;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 6, 0, Math.PI * 2);
          ctx.fill();
        }

        // Star core
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.cr}, ${s.cg}, ${s.cb}, ${alpha})`;
        ctx.fill();
      });

      // Shooting stars
      if (Math.random() < 0.006) {
        shootingStars.push({
          x: Math.random() * w * 0.6 + w * 0.2,
          y: Math.random() * h * 0.3,
          len: Math.random() * 80 + 50,
          speed: Math.random() * 5 + 3,
          angle: Math.PI * 0.6 + (Math.random() - 0.5) * 0.4,
          life: 1,
          decay: Math.random() * 0.015 + 0.008,
        });
      }
      shootingStars = shootingStars.filter(ss => ss.life > 0);
      shootingStars.forEach(ss => {
        // Head moves in angle direction; tail trails behind
        const tailDx = -Math.cos(ss.angle) * ss.len;
        const tailDy = -Math.sin(ss.angle) * ss.len;
        const grad = ctx.createLinearGradient(ss.x, ss.y, ss.x + tailDx, ss.y + tailDy);
        grad.addColorStop(0, `rgba(220, 230, 255, ${ss.life})`);
        grad.addColorStop(0.3, `rgba(180, 200, 255, ${ss.life * 0.7})`);
        grad.addColorStop(1, 'rgba(180, 200, 255, 0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(ss.x, ss.y);
        ctx.lineTo(ss.x + tailDx, ss.y + tailDy);
        ctx.stroke();

        // Head glow
        const headGrad = ctx.createRadialGradient(ss.x, ss.y, 0, ss.x, ss.y, 4);
        headGrad.addColorStop(0, `rgba(255, 255, 255, ${ss.life * 0.8})`);
        headGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = headGrad;
        ctx.beginPath();
        ctx.arc(ss.x, ss.y, 4, 0, Math.PI * 2);
        ctx.fill();

        ss.x += Math.cos(ss.angle) * ss.speed;
        ss.y += Math.sin(ss.angle) * ss.speed;
        ss.life -= ss.decay;
      });

      // Subtle warm glow from bottom (campfire ambiance)
      const campGlow = ctx.createRadialGradient(w * 0.5, h * 1.1, 0, w * 0.5, h * 1.1, h * 0.7);
      campGlow.addColorStop(0, 'rgba(196, 157, 77, 0.04)');
      campGlow.addColorStop(0.5, 'rgba(140, 80, 30, 0.02)');
      campGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = campGlow;
      ctx.fillRect(0, 0, w, h);

      animId = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }} />;
};

const SERVICE_FEE = 5;

// === TICKET CARD ===
const TicketCard = ({ ticket, onBuy, buying }) => {
  const [qty, setQty] = useState(1);
  const available = ticket.capacity > 0 ? ticket.capacity - ticket.sold : null;
  const soldOut = available !== null && available <= 0;
  const maxQty = available !== null ? Math.min(available, 10) : 10;
  const subtotal = ticket.price * qty;
  const fees = SERVICE_FEE * qty;
  const total = subtotal + fees;

  return (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 p-6 flex flex-col ${soldOut ? 'opacity-50' : ''}`} data-testid={`ticket-type-${ticket.id}`}>
      <h3 className="text-lg font-semibold text-white mb-2">{ticket.name}</h3>
      {ticket.description && <p className="text-gray-400 text-sm mb-4 flex-1">{ticket.description}</p>}
      <div className="mt-auto">
        <p className="text-2xl font-bold text-brand-gold mb-0">
          {ticket.price === 0 ? 'Free' : `$${ticket.price.toFixed(2)}`}
        </p>
        <p className="text-gray-500 text-xs mb-3">+ $5.00 service fee per ticket</p>
        {!soldOut && (
          <>
            <div className="flex items-center gap-3 mb-3" data-testid={`qty-selector-${ticket.id}`}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-8 h-8 bg-white/10 text-white border border-white/20 flex items-center justify-center hover:bg-white/20 text-lg font-bold">-</button>
              <span className="text-white font-semibold text-lg w-8 text-center tabular-nums">{qty}</span>
              <button onClick={() => setQty(q => Math.min(maxQty, q + 1))} className="w-8 h-8 bg-white/10 text-white border border-white/20 flex items-center justify-center hover:bg-white/20 text-lg font-bold">+</button>
            </div>
            {qty >= 1 && (
              <div className="text-xs text-gray-400 mb-4 space-y-1">
                <div className="flex justify-between"><span>Tickets ({qty}x ${ticket.price.toFixed(2)})</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Service fee ({qty}x $5.00)</span><span>${fees.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-white/10 pt-1 text-white font-medium"><span>Total</span><span>${total.toFixed(2)}</span></div>
              </div>
            )}
          </>
        )}
        {!soldOut ? (
          <button
            onClick={() => onBuy(ticket, qty)}
            disabled={buying}
            className="neon-btn w-full justify-center text-sm py-3"
            data-testid={`buy-ticket-${ticket.id}`}
          >
            <span className="neon-btn-glow" />
            <Ticket className="w-4 h-4" />
            {buying ? 'Processing...' : `Get ${qty > 1 ? qty + ' ' : ''}Ticket${qty > 1 ? 's' : ''}`}
          </button>
        ) : (
          <span className="block text-center bg-gray-800 text-gray-500 font-medium py-3 px-4 text-sm">Sold Out</span>
        )}
      </div>
    </div>
  );
};

// === COUNTDOWN TIMER ===
const EVENT_DATE = new Date('2026-08-21T12:00:00');
const Countdown = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = EVENT_DATE - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  const units = [
    { label: 'Days', value: days },
    { label: 'Hours', value: hours },
    { label: 'Minutes', value: minutes },
    { label: 'Seconds', value: seconds },
  ];

  return (
    <div className="flex justify-center gap-3 sm:gap-5" data-testid="countdown-timer">
      {units.map((u) => (
        <div key={u.label} className="text-center">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 w-16 sm:w-20 py-3 sm:py-4">
            <p className="text-2xl sm:text-3xl font-bold text-white tabular-nums">{String(u.value).padStart(2, '0')}</p>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 uppercase tracking-wider">{u.label}</p>
        </div>
      ))}
    </div>
  );
};

// === TESTIMONIALS ===
const testimonials = [
  { text: "Last year changed my life. The connections I made around that campfire are still some of the strongest I have.", name: "2025 Attendee" },
  { text: "I came alone and left with a family. The workshops, the energy, the stars. Nothing compares.", name: "2025 Attendee" },
  { text: "I didn't know how much I needed this until I was sitting under those stars feeling heard for the first time in years.", name: "2025 Attendee" },
];

// === MAIN PAGE ===
const ElevatedEscapeTicketsPage = () => {
  usePageTitle('Elevated Escape 2026 - Tickets');
  usePageMeta({
    title: 'Elevated Escape 2026 - Camping Retreat',
    description: 'The 2nd Annual Elevated Escape — a weekend camping retreat of music, community, nature & good energy. August 21-23, 2026 at Camp Waub-O-Jeeg, Taylors Falls, MN. Featuring NUR-D, Sam Licari, MPLS Drew & more. 21+ only.',
    image: '/images/site/sponsor.jpg',
    url: 'https://elevatingmn.org/elevated-escape/tickets',
  });
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [salesSettings, setSalesSettings] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketsRes, settingsRes] = await Promise.all([
          fetch(`${API}/ticket-types?event_id=elevated-escape-2026`),
          fetch(`${API}/ticket-settings?event_id=elevated-escape-2026`)
        ]);
        if (ticketsRes.ok) setTicketTypes(await ticketsRes.json());
        if (settingsRes.ok) setSalesSettings(await settingsRes.json());
      } catch (err) { console.error('Error:', err); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const salesOpen = salesSettings ? salesSettings.sales_enabled !== false : true;

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    setPromoError('');
    setPromoResult(null);
    try {
      const res = await fetch(`${API}/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, ticket_type_id: ticketTypes[0]?.id || '', quantity: 1 })
      });
      if (res.ok) {
        setPromoResult(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setPromoError(err.detail || 'Invalid code');
        setPromoResult(null);
      }
    } catch { setPromoError('Could not validate code'); }
    setPromoChecking(false);
  };

  const handleBuyTicket = async (ticket, quantity) => {
    setBuying(true);
    try {
      const res = await fetch(`${API}/checkout/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_type_id: ticket.id,
          quantity: quantity || 1,
          origin_url: window.location.origin,
          discount_code: promoResult?.code || ''
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) { window.location.href = data.url; return; }
      }
      const err = await res.json().catch(() => ({}));
      console.error('Checkout error:', err);
      alert(err.detail || 'Could not start checkout. Please try again.');
    } catch (err) {
      alert('Connection error. Please try again.');
    }
    setBuying(false);
  };

  return (
    <div className="bg-[#030012] min-h-screen" data-testid="elevated-escape-tickets-page">
      <AmbientAudio />

      {/* Hero with Galactic Sky */}
      <section className="relative overflow-hidden" style={{ minHeight: '90vh' }}>
        <GalacticSky />

        <div className="relative mx-auto max-w-5xl px-6 lg:px-8 pt-20 pb-16 flex flex-col items-center text-center" style={{ zIndex: 2 }}>
          <p className="text-brand-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 animate-pulse">
            2nd Annual
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 tracking-tight" style={{ textShadow: '0 0 40px rgba(100, 60, 200, 0.3), 0 0 80px rgba(60, 40, 160, 0.15)' }}>
            The Elevated Escape
          </h1>
          <p className="text-xl sm:text-2xl text-brand-gold font-light mb-8">
            Camping Retreat 2026
          </p>

          <div className="flex flex-wrap justify-center gap-6 text-gray-300 text-sm mb-6">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-brand-gold" /> August 21-23, 2026</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-gold" /> Camp Waub-O-Jeeg, Taylors Falls, MN</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-brand-gold" /> Limited to 300 Attendees</span>
          </div>

          <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/40 px-4 py-2 mb-8" data-testid="age-restriction-badge">
            <span className="text-red-400 font-bold text-sm">21+ ONLY</span>
            <span className="text-gray-400 text-xs">Must be 21 years or older to attend</span>
          </div>

          <p className="text-gray-400 max-w-2xl text-base leading-relaxed mb-3">
            A weekend of music, community, nature & good energy. Unplug. Reset. Reconnect.
          </p>
          <p className="text-gray-500 max-w-xl text-sm leading-relaxed mb-6">
            Featuring <span className="text-white font-medium">NUR-D</span>, <span className="text-white font-medium">Sam Licari</span>, <span className="text-white font-medium">MPLS Drew</span>, <span className="text-white font-medium">Will Effertz</span>, <span className="text-white font-medium">DJ Mystery Machine</span> and more.
          </p>
          <p className="text-brand-gold/70 text-sm font-medium mb-6">You are not alone.</p>

          {/* Countdown */}
          <div className="mb-10">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">Event starts in</p>
            <Countdown />
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#tickets" className="neon-btn" data-testid="hero-get-tickets-btn">
              <span className="neon-btn-glow" />
              <Ticket className="w-5 h-5" /> Get Tickets
            </a>
            <a href={FB_EVENT} target="_blank" rel="noopener noreferrer" className="border border-white/20 text-white font-semibold px-8 py-4 text-base hover:bg-white/10 transition-all flex items-center gap-2" data-testid="fb-event-btn">
              <ExternalLink className="w-5 h-5" /> Facebook Event
            </a>
          </div>
        </div>
      </section>

      {/* Sponsor Image */}
      <section className="relative py-16 bg-[#030012]">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 space-y-8">
          <a href={FB_EVENT} target="_blank" rel="noopener noreferrer" className="block group" data-testid="sponsor-image-link">
            <div className="relative overflow-hidden border border-brand-gold/20 hover:border-brand-gold/50 transition-all">
              <img src={SPONSOR_IMG} alt="Elevated Escape Camping Retreat 2026" className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-brand-gold text-brand-dark font-bold px-6 py-3 text-sm">View on Facebook</span>
              </div>
            </div>
          </a>

          {/* All-Inclusive Highlight + Meal Menu */}
          <div className="relative p-[3px] neon-border-wrap">
            <div className="absolute inset-0 neon-border-glow" />
            <div className="relative bg-[#0a0520] p-6 lg:p-8 text-center">
              <p className="text-brand-gold text-xs uppercase tracking-widest mb-2">Your Weekend Pass Includes</p>
              <h3 className="text-2xl font-bold text-white mb-3">All-Inclusive Experience</h3>
              <p className="text-gray-300 text-sm max-w-xl mx-auto mb-6">Your ticket covers everything — 3 community meals, all workshops, live music, yoga, creative sessions, and more. No hidden costs. Just show up and enjoy.</p>
            </div>
          </div>

          <div className="overflow-hidden border border-brand-gold/20" data-testid="meal-menu-image">
            <img src="/images/site/mealmenu.jpg" alt="Elevated Escape 2026 Weekend Meal Menu - All meals included with weekend pass" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Get Tickets CTA */}
      <section className="py-10 bg-[#030012]">
        <div className="text-center">
          <a href="#tickets" className="neon-btn" data-testid="mid-get-tickets-btn">
            <span className="neon-btn-glow" />
            <Ticket className="w-5 h-5" /> Get Tickets
          </a>
        </div>
      </section>

      {/* Featured Performances */}
      <section className="py-20 bg-[#06021a]" data-testid="featured-artists">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <p className="text-brand-gold text-xs uppercase tracking-widest text-center mb-3">Live Music Featuring</p>
          <h2 className="text-2xl sm:text-3xl font-semibold text-white text-center mb-4">Featured Performances</h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            NUR-D, Sam Licari, MPLS Drew, Will Effertz, DJ Mystery Machine & more artists to be announced.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { id: 'UMnnFZKMv4g', artist: 'NUR-D' },
              { id: 'G8bq23AwMUw', artist: 'Sam Licari' },
              { id: '6pGHc9Q_4W4', artist: 'MPLS Drew' },
              { id: 'VrI7-bTeNe0', artist: 'Will Effertz' },
            ].map((v) => (
              <div key={v.id} className="group">
                <div className="aspect-video bg-black/50 border border-white/10 overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${v.id}`}
                    title={v.artist}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    loading="lazy"
                  />
                </div>
                <p className="text-white font-medium text-sm mt-3">{v.artist}</p>
              </div>
            ))}
          </div>

          {/* Get Tickets CTA */}
          <div className="text-center mt-12">
            <a href="#tickets" className="neon-btn" data-testid="artists-get-tickets-btn">
              <span className="neon-btn-glow" />
              <Ticket className="w-5 h-5" /> Get Tickets
            </a>
          </div>
        </div>
      </section>

      {/* Sponsors, Artists & Contributors - 3D Cylinder */}
      <SponsorCylinder />

      {/* Testimonials */}
      <section className="py-16 bg-[#06021a]">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <p className="text-brand-gold text-xs uppercase tracking-widest text-center mb-8">From Last Year&apos;s Attendees</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-6">
                <Quote className="w-5 h-5 text-brand-gold/40 mb-3" />
                <p className="text-gray-300 text-sm leading-relaxed mb-4 italic">&ldquo;{t.text}&rdquo;</p>
                <p className="text-brand-gold/70 text-xs font-medium">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Last Year's Moments */}
      <section className="py-8 bg-[#030012] overflow-hidden">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4" data-testid="retreat-photos">
          {[
            { src: '/images/site/gallery01.jpg', alt: 'Group photo at camp' },
            { src: '/images/site/gallery05.jpg', alt: 'Hanging out at camp' },
            { src: '/images/site/gallery02.jpg', alt: 'Live music at night' },
            { src: '/images/site/gallery03.jpg', alt: 'Trail with dog' },
          ].map((img, i) => (
            <div key={i} className="flex-shrink-0">
              <img src={img.src} alt={img.alt} className="h-40 sm:h-52 w-auto object-cover opacity-80 hover:opacity-100 transition-opacity" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-[#06021a]">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white text-center mb-4">The Experience</h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">A weekend of music, community, nature & good energy. Limited to 300 attendees.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { name: 'Live Bands & DJ Sets', icon: '🎵' },
              { name: 'Workshops & Experiences', icon: '🎯' },
              { name: 'Puff N Paint & Creative Space', icon: '🎨' },
              { name: 'Coffee & Tea Bar', icon: '☕' },
              { name: 'Morning Yoga', icon: '🧘' },
              { name: 'Networking & Connection', icon: '🤝' },
              { name: '4 Community Fires', icon: '🔥' },
            ].map((item) => (
              <div key={item.name} className="bg-white/5 border border-white/10 p-5 text-center">
                <span className="text-2xl mb-3 block">{item.icon}</span>
                <p className="text-white text-sm font-medium">{item.name}</p>
              </div>
            ))}
            {/* 3 Community Meals - emphasized */}
            <div className="bg-brand-gold/10 border-2 border-brand-gold/40 p-5 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-brand-gold text-brand-dark text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider">Included</div>
              <span className="text-2xl mb-3 block">🍽</span>
              <p className="text-brand-gold text-sm font-bold">3 Community Meals</p>
              <p className="text-gray-400 text-[10px] mt-1">Breakfast, Lunch & Dinner</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { name: 'Kava Lounge', icon: '🍵' },
              { name: 'Breathwork Sessions', icon: '🌬' },
              { name: 'Sound Baths & Wellness', icon: '🔊' },
              { name: 'Relaxation Zones', icon: '🌿' },
              { name: 'Nights Under the Stars', icon: '⭐' },
              { name: 'New Friends & Good Vibes', icon: '✌️' },
              { name: 'Memories to Last', icon: '📸' },
              { name: '& So Much More!', icon: '✨' },
            ].map((item) => (
              <div key={item.name} className="bg-white/5 border border-white/10 p-4 text-center">
                <span className="text-xl mb-2 block">{item.icon}</span>
                <p className="text-white text-xs font-medium">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tickets Section */}
      <section className="py-20 bg-[#030012] relative" id="tickets">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <Star className="w-8 h-8 text-brand-gold mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-3">Get Your Tickets</h2>
            <p className="text-gray-400 max-w-xl mx-auto">Secure your spot for the 2nd Annual Elevated Escape. Payments processed securely via Stripe.</p>
            <p className="text-brand-gold/80 text-sm mt-3 font-medium">Weekend passes include 3 community meals (breakfast, lunch & dinner)</p>
            <p className="text-red-400/80 text-xs mt-2 font-semibold">This is a 21+ only event. Valid ID required at check-in.</p>
          </div>

          {loading ? (
            <p className="text-gray-500 text-center">Loading tickets...</p>
          ) : !salesOpen ? (
            <div className="text-center py-12" data-testid="sales-closed-message">
              <div className="bg-white/5 border border-white/10 p-8 max-w-lg mx-auto">
                <p className="text-gray-300 text-lg mb-3">{salesSettings?.closed_message || 'Ticket sales are currently closed.'}</p>
                <p className="text-gray-500 text-sm mb-6">Follow us on Facebook to be the first to know when sales open.</p>
                <a href={FB_EVENT} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-brand-gold/40 text-brand-gold px-6 py-3 text-sm font-semibold hover:bg-brand-gold/10 transition-colors">
                  <ExternalLink className="w-4 h-4" /> Follow on Facebook
                </a>
              </div>
            </div>
          ) : ticketTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg mb-2">Tickets coming soon.</p>
              <p className="text-gray-500 text-sm mb-6">Follow us on Facebook to be the first to know.</p>
              <a href={FB_EVENT} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-brand-gold/40 text-brand-gold px-6 py-3 text-sm font-semibold hover:bg-brand-gold/10 transition-colors">
                <ExternalLink className="w-4 h-4" /> Follow on Facebook
              </a>
            </div>
          ) : (
            <>
              {/* Promo Code */}
              <div className="max-w-md mx-auto mb-10" data-testid="promo-code-section">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                    placeholder="Promo code"
                    className="flex-1 bg-white/5 border border-white/20 text-white px-4 py-3 text-sm placeholder:text-gray-500 focus:border-brand-gold focus:outline-none font-mono"
                    data-testid="promo-code-input"
                  />
                  <button
                    onClick={applyPromoCode}
                    disabled={promoChecking || !promoCode.trim()}
                    className="bg-white/10 border border-white/20 text-white px-5 py-3 text-sm font-medium hover:bg-white/20 disabled:opacity-40 transition-colors"
                    data-testid="apply-promo-btn"
                  >
                    {promoChecking ? '...' : 'Apply'}
                  </button>
                </div>
                {promoResult && (
                  <p className="text-green-400 text-sm mt-2" data-testid="promo-success">
                    Code <span className="font-mono font-bold">{promoResult.code}</span> applied: {promoResult.discount_type === 'percent' ? `${promoResult.discount_value}% off` : `$${promoResult.discount_value.toFixed(2)} off`}
                  </p>
                )}
                {promoError && <p className="text-red-400 text-sm mt-2" data-testid="promo-error">{promoError}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ticket-types-grid">
                {ticketTypes.map(t => (
                  <TicketCard key={t.id} ticket={t} onBuy={handleBuyTicket} buying={buying} />
                ))}
              </div>

            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#06021a] border-t border-white/5">
        <div className="mx-auto max-w-3xl px-6 lg:px-8 text-center">
          <p className="text-gray-400 mb-4">Questions about the event or sponsorship opportunities?</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="mailto:admin@elevatingmn.com" className="text-brand-gold font-medium hover:underline">admin@elevatingmn.com</a>
            <span className="text-gray-600">|</span>
            <a href={FB_EVENT} target="_blank" rel="noopener noreferrer" className="text-brand-gold font-medium hover:underline flex items-center gap-1">
              Facebook Event <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ElevatedEscapeTicketsPage;
